import * as React from 'react';
import { Select as BaseSelect } from '@base-ui-components/react/select';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { PopupCloseProvider, useCloseWhenAnchorHidden, usePopupOpen } from '@/shared/lib/popupAnchor';
import { useFieldContext } from '../Field/FieldContext';
import { Scroller } from '@/shared/ui/Scroller';
import styles from './Select.module.css';

/**
 * Select поверх Base UI (Luma). Два уровня API:
 *
 * 1) Высокоуровневый — для простых случаев (фильтры, формы):
 *
 *    <Select
 *      value={status}
 *      onValueChange={setStatus}
 *      placeholder="Все статусы"
 *      options={[{ value: 'new', label: 'Новая' }, …]}
 *    />
 *
 * 2) Примитивы (SelectRoot/SelectTrigger/…) — когда нужна кастомная разметка.
 *
 * @use выбор одного значения из короткого списка без поиска.
 */

export interface SelectOption {
  value: string;
  label: React.ReactNode;
  disabled?: boolean;
}

export interface SelectProps
  extends Omit<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    'value' | 'defaultValue' | 'onChange' | 'children'
  > {
  value: string;
  onValueChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: React.ReactNode;
  disabled?: boolean;
  className?: string;
}

export function SelectRoot(props: React.ComponentProps<typeof BaseSelect.Root>) {
  const { open, onOpenChange, close } = usePopupOpen(props);
  return (
    <PopupCloseProvider value={close}>
      <BaseSelect.Root {...props} open={open} onOpenChange={onOpenChange} />
    </PopupCloseProvider>
  );
}
export const SelectValue = BaseSelect.Value;
export const SelectGroup = BaseSelect.Group;
export const SelectGroupLabel = BaseSelect.GroupLabel;

export const SelectTrigger = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<typeof BaseSelect.Trigger>
>(function SelectTrigger({ className, children, ...rest }, ref) {
  return (
    <BaseSelect.Trigger ref={ref} className={cn(styles.trigger, className)} {...rest}>
      {children}
      <BaseSelect.Icon className={styles.icon}>
        <ChevronDown size={14} />
      </BaseSelect.Icon>
    </BaseSelect.Trigger>
  );
});

export const SelectContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof BaseSelect.Popup>
>(function SelectContent({ className, children, ...rest }, ref) {
  const positionerRef = useCloseWhenAnchorHidden();
  return (
    <BaseSelect.Portal>
      { }
      <BaseSelect.Positioner
        sideOffset={6}
        side="bottom"
        align="start"
        alignItemWithTrigger={false}
        className={styles.positioner}
        ref={positionerRef}
      >
        <BaseSelect.Popup ref={ref} className={cn(styles.popup, className)} {...rest}>
          <Scroller className={styles.scroller} viewportClassName={styles.viewport}>
            {children}
          </Scroller>
        </BaseSelect.Popup>
      </BaseSelect.Positioner>
    </BaseSelect.Portal>
  );
});

export const SelectItem = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof BaseSelect.Item>
>(function SelectItem({ className, children, ...rest }, ref) {
  return (
    <BaseSelect.Item ref={ref} className={cn(styles.item, className)} {...rest}>
      <BaseSelect.ItemIndicator className={styles.indicator}>
        <Check size={13} />
      </BaseSelect.ItemIndicator>
      <BaseSelect.ItemText className={styles.itemText}>{children}</BaseSelect.ItemText>
    </BaseSelect.Item>
  );
});

export const Select: React.FC<SelectProps> = ({
  value,
  onValueChange,
  options,
  placeholder,
  disabled,
  className,
  ...rest
}) => {
  const selected = value === '' ? undefined : options.find((o) => o.value === value);
  const field = useFieldContext();

  const labelFor = React.useCallback(
    (v: unknown): React.ReactNode => {
      const found = options.find((o) => o.value === v);
      if (found) return found.label;
      return <span className={styles.placeholder}>{placeholder ?? 'Выбрать…'}</span>;
    },
    [options, placeholder],
  );
  return (
    <SelectRoot value={value} onValueChange={(v) => onValueChange(String(v ?? ''))} disabled={disabled}>
      { }
      { }
      <SelectTrigger
        className={className}
        data-filled={selected ? '' : undefined}
        {...rest}
        id={rest.id ?? field?.controlId}
      >
        <SelectValue className={styles.value}>{labelFor}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value} disabled={o.disabled}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </SelectRoot>
  );
};
